import React, { useMemo, useState, useEffect } from 'react';
import { history, useIntl } from 'umi';
import moment from 'moment';
import { useDebounceFn } from 'ahooks';
import { Segment, Justify, SearchBox } from 'tea-component';
import Loading from '@intelligence/components/Loading';
import InsightsReportCard from '@intelligence/components/ReportCard/InsightsReportCard';
import { getReportByIdApi } from '@intelligence/services/reportService';
import { isEmpty } from '@intelligence/v3/utils/common';
import { formatDateStr } from '@intelligence/v3/utils/date';
import { reportButtonLog } from '@intelligence/v3/utils/logReporter';
import { dbiBtnLogMes } from '@intelligence/utils/buriedPointCfg';
import { splitReportTagStr } from '@intelligence/v3/utils/topcharts';
import { LANGUAGE_CN } from '@home/_config/config';
import { DEBOUNCE_CONFIG } from '@intelligence/v3/_config/utils';
import { DATABRAIN_COS_ACCELERATE, DATABRAIN_COS_DOMAIN } from '@intelligence/_config/config';
import { pageReportTypeConfig } from '@intelligence/pages/InsightsReport/_config';
import { buildReportAuthors } from '@intelligence/components/ReportCard/authors';

import type { ICasualGamesReportsCommonProps } from '../../types';
import type { IAnalysisReportItem, IProcessedAnalysisReportItem } from './types';

import '@intelligence/assets/scss/BusinessReports/index.scss';
import '../index.scss';

const casualGamesHubAnalysisReportTypeConfigKeyMap: IRecord = {
  intelligence_ai: pageReportTypeConfig.aiHubReports,
  intelligence: pageReportTypeConfig.aiHubReports,
  intelligence_insights: pageReportTypeConfig.insightsReports,
  intelligence_iic: pageReportTypeConfig.insightsCenterLibrary,
  gameResearch: pageReportTypeConfig.gameResearchReport,
};
const copyEmailLogConfig = dbiBtnLogMes.casualGamesReportsAnalysisReportsCopyEmail;
const transformAuthorAvatar = (avatar: string): string => avatar.replace(DATABRAIN_COS_DOMAIN, DATABRAIN_COS_ACCELERATE);

const processReportData = (reports?: IAnalysisReportItem[] | null): IProcessedAnalysisReportItem[] => {
  if (isEmpty(reports)) {
    return [];
  }

  return reports!.map((item) => {
    const coverImg = item?.cover?.replace(DATABRAIN_COS_DOMAIN, DATABRAIN_COS_ACCELERATE) || '';
    const itemTitle = item?.title || '';
    const itemTitleEN = item?.title_en || '';
    const itemTag = splitReportTagStr(item?.tag);
    const itemTagEN = splitReportTagStr(item?.tag_en);

    return {
      ...item,
      key: item?.resource_id || '',
      coverImg,
      title: [itemTitle, itemTitleEN],
      originTitle: [itemTitle, itemTitleEN],
      report_type: [item?.report_type || '', item?.report_type_en || ''],
      summary: [item?.introduction || '', item?.introduction_en || ''],
      tag: [itemTag, itemTagEN],
      tag_en: itemTagEN,
      authors: buildReportAuthors(item, { transformAvatar: transformAuthorAvatar }),
      source: item?.source || '',
      report_like: item?.report_like || 0,
      time: formatDateStr(item?.create_time),
      update_time: item?.update_time,
      create_time: item?.create_time,
    };
  });
};

type TAnalysisReportsTabProps = ICasualGamesReportsCommonProps;

const AnalysisReportsTab: React.FC<TAnalysisReportsTabProps> = (props) => {
  const { loading: loadingProps, reportIds } = props;

  const { locale, formatMessage } = useIntl();

  const [loading, setLoading] = useState(true);
  const [segmentValue, setSegmentValue] = useState('all');
  const [searchValue, setSearchValue] = useState('');
  const [allReports, setAllReports] = useState<IProcessedAnalysisReportItem[]>([]);

  const languageSegmentList = useMemo(() => {
    const list: { value: string; text: string; }[] = [
      { value: 'all', text: formatMessage({ id: 'dbi_reports_all' }) as string },
    ];

    if (!isEmpty(allReports)) {
      const hasEN = !!allReports?.some((i) => {
        return String((i as any)?.pdf_en || '').trim() !== '';
      });
      const hasCN = !!allReports?.some((i) => {
        return String((i as any)?.pdf || '').trim() !== '';
      });

      if (hasEN) {
        list.push({ value: 'englishReport', text: formatMessage({ id: 'dbi_segment_english_report' }) as string });
      }
      if (hasCN) {
        list.push({ value: 'chineseReport', text: formatMessage({ id: 'dbi_segment_chinese_report' }) as string });
      }
    }

    return list;
  }, [locale, allReports]);

  const reportLanguage = useMemo(() => {
    if (segmentValue && segmentValue !== 'all') {
      return segmentValue;
    }

    return locale === LANGUAGE_CN ? 'chineseReport' : 'englishReport';
  }, [locale, segmentValue]);

  const lang = useMemo(() => (reportLanguage === 'chineseReport' ? 0 : 1), [reportLanguage]);

  const handleSearch = (value: string) => {
    const q = value?.trim()?.toLowerCase() || '';
    if (!q) {
      return;
    }

    const fileKey = reportLanguage === 'chineseReport' ? 'pdf' : 'pdf_en';
    const inCurrentLang = allReports?.filter((r) => {
      return String((r as any)?.[fileKey] || '').trim() !== '';
    });
    const currentMatches = inCurrentLang?.filter((r) => {
      return !!r?.title?.[lang]?.toLowerCase()?.includes(q);
    });
    if (!!currentMatches?.length) {
      return;
    }

    for (const opt of languageSegmentList) {
      const optValue = opt?.value || '';
      if (optValue === 'all' || optValue === reportLanguage) {
        continue;
      }
      const fk = optValue === 'chineseReport' ? 'pdf' : 'pdf_en';
      const subset = allReports?.filter((r) => {
        return String((r as any)?.[fk] || '')?.trim() !== '';
      });
      const matches = subset?.filter((r) => {
        return (r?.title?.[optValue === 'chineseReport' ? 0 : 1] || '')?.toLowerCase()?.includes(q);
      });
      if (!!matches?.length) {
        setSegmentValue(optValue);
        break;
      }
    }
  };

  const { run: debouncedSearch } = useDebounceFn(handleSearch, DEBOUNCE_CONFIG);

  const onSearchChange = (value: string) => {
    setSearchValue(value);
    debouncedSearch(value);
  };

  const filteredReports = useMemo(() => {
    if (isEmpty(allReports)) return [];

    const fileKey = reportLanguage === 'chineseReport' ? 'pdf' : 'pdf_en';
    let reports = allReports?.filter((r) => {
      return String((r as any)?.[fileKey] || '')?.trim() !== '';
    });

    if (!!searchValue?.trim()) {
      const q = searchValue?.trim()?.toLowerCase() || '';
      reports = reports?.filter((r) => {
        return (r?.title?.[lang] || '')?.toLowerCase()?.includes(q);
      });
    }

    return reports || [];
  }, [lang, reportLanguage, searchValue, allReports]);

  useEffect(() => {
    if (loadingProps) {
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await getReportByIdApi({ resource_ids: reportIds });
        const processed = processReportData(res?.resource_list);
        setAllReports(processed);
      } catch (error) {
        console.error('Failed to fetch report data:', error);
      } finally {
        setLoading(false);
      }
    };

    void fetchData();
  }, [loadingProps, reportIds]);

  const renderReportListElement = () => {
    if (isEmpty(filteredReports)) {
      return <div className="dbi-casual-report-placeholder">{formatMessage({ id: 'dbi_reports_search_na_data' })}</div>;
    }

    return filteredReports!.map((item) => (
      <InsightsReportCard
        needPopover
        showContactInfo
        key={item?.resource_id}
        downloadLogConfigKey="casualGamesReportsAnalysisReportsDownloadReport"
        // @ts-ignore
        data={item}
        lang={lang}
        reportLangSegment={segmentValue}
        shareBack={history.location.query?.id as string}
        reportTypeConfig={{ ...casualGamesHubAnalysisReportTypeConfigKeyMap?.[item?.system], pagePathCur: '' }}
        pdfPreviewDownloadPage="/pdfPreviewDownloadLikeShare"
        onChange={() => {}}
        onViewsChange={() => {}}
        isNew={moment(item?.time)?.isAfter(moment()?.subtract(1, 'month'))}
        onCopyIconClick={(email) => {
          reportButtonLog({
            ...copyEmailLogConfig,
            extInfo: `Casual Games Hub / Reports / Analysis Reports页面复制作者邮箱(${item?.resource_id})(${email})`,
          });
        }}
      />
    ));
  };

  if (loading) {
    return <Loading content={'Loading...'} />;
  }

  return (
    <div className="dbi-businessreports dbi-market-report-full">
      <Justify
        className="dbi-businessreports-justify"
        left={
          <Segment
            className="segment-light"
            value={segmentValue}
            options={languageSegmentList}
            onChange={(value: string) => setSegmentValue(value)}
          />
        }
        right={
          <SearchBox
            placeholder={formatMessage({ id: 'dbi_reports_search_placeholder' })}
            value={searchValue}
            onChange={onSearchChange}
          />
        }
      />
      <div style={{ marginTop: '16px' }}>{renderReportListElement()}</div>
    </div>
  );
};

export default AnalysisReportsTab;
